import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Minus, Plus, CheckCircle2, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { EVENT, IMAGES, BANK } from "@/lib/siteData";
import { StiftungChance } from "@/components/site/StiftungChance";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTICIPATION = [
  "Einzelperson",
  "Begleitung / Paar",
  "Tisch (8 Personen)",
  "Firmensponsoring",
];

const PRICE_BASE = 80;
const PRICE_TEDDY = 100;

export const RegistrationForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", participation: "Einzelperson", teddy: false, guests: 1, message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const perPerson = form.teddy ? PRICE_TEDDY : PRICE_BASE;
  const total = perPerson * form.guests;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Bitte Name und E-Mail ausfüllen.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/registrations`, form);
      setDone(true);
      toast.success("Vielen Dank! Ihre Anmeldung ist eingegangen.");
    } catch (err) {
      toast.error("Es ist ein Fehler aufgetreten. Bitte erneut versuchen.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-transparent border-b border-ink/20 py-3 text-ink placeholder-ink/35 focus:border-magenta focus:outline-none transition-colors duration-300";

  return (
    <section id="anmelden" className="relative bg-ivory px-4 sm:px-6 py-28 sm:py-36" data-testid="registration-section">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-16 lg:grid-cols-2 lg:gap-24">
        <div>
          <p className="mb-5 text-xs uppercase tracking-[0.35em] text-violet">Anmeldung</p>
          <h2 className="font-display text-4xl sm:text-6xl tracking-tight text-ink leading-[1.02]">
            Seien Sie <span className="italic text-magenta">dabei</span>.
          </h2>
          <p className="mt-8 max-w-md text-lg leading-relaxed text-ink/60">
            Reservieren Sie Ihren Platz am Benefizabend. Wir melden uns mit allen Details bei Ihnen.
          </p>
          <div className="mt-10 space-y-3 text-ink/70">
            <p className="font-display text-2xl text-ink">{EVENT.date}</p>
            <p>{EVENT.time} · {EVENT.location}, {EVENT.city}</p>
          </div>

          {/* Preise */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="pricing-block">
            <div className="rounded-2xl border border-ink/10 bg-white p-6">
              <p className="text-xs uppercase tracking-[0.25em] text-ink/40">Pro Person</p>
              <p className="mt-2 font-display text-4xl text-ink">CHF 80</p>
              <p className="mt-2 text-sm text-ink/60">inkl. Apéro Riche</p>
            </div>
            <div className="relative overflow-hidden rounded-2xl border border-magenta/30 bg-gradient-to-br from-violet/10 to-magenta/10 p-6">
              <p className="text-xs uppercase tracking-[0.25em] text-magenta">Mit Teddybär</p>
              <p className="mt-2 font-display text-4xl text-ink">CHF 100</p>
              <p className="mt-2 pr-20 text-sm text-ink/60">inkl. Apéro Riche &amp;<br />Teddybär</p>
              <img
                src={IMAGES.teddy}
                alt="Benefiz-Teddybär mit JCI × Stiftung Chance Shirt"
                className="pointer-events-none absolute -bottom-3 -right-3 h-24 w-24 object-contain drop-shadow-lg sm:h-28 sm:w-28"
                data-testid="teddy-image"
              />
            </div>
          </div>

          <ul className="mt-10 space-y-3 border-t border-ink/10 pt-8">
            {[
              <>Reinerlös vollumfänglich für die <StiftungChance testid="registration-stiftung-link" /></>,
              "Unverbindliche Reservation – wir bestätigen per E-Mail",
              "Für Einzelgäste, Paare, Tische & Firmen",
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-ink/70">
                <Sparkles size={16} className="mt-1 shrink-0 text-magenta" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative">
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex h-full min-h-[320px] flex-col items-center justify-center rounded-3xl border border-ink/10 bg-white p-10 text-center"
              data-testid="registration-success"
            >
              <CheckCircle2 size={48} className="text-magenta" />
              <h3 className="font-display text-3xl text-ink mt-6">Herzlichen Dank!</h3>
              <p className="text-ink/60 mt-3 max-w-sm">
                Ihre Anmeldung ist bei uns eingegangen. Wir freuen uns, Sie am Anlass begrüssen zu dürfen.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="space-y-8" data-testid="registration-form">
              <div>
                <label className="text-xs uppercase tracking-[0.25em] text-ink/40">Name</label>
                <input
                  type="text" value={form.name} onChange={(e) => update("name", e.target.value)}
                  placeholder="Ihr vollständiger Name" className={field} data-testid="input-name"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.25em] text-ink/40">E-Mail</label>
                <input
                  type="email" value={form.email} onChange={(e) => update("email", e.target.value)}
                  placeholder="ihre@email.ch" className={field} data-testid="input-email"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.25em] text-ink/40">Telefon (optional)</label>
                <input
                  type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)}
                  placeholder="+41 79 000 00 00" className={field} data-testid="input-phone"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.25em] text-ink/40 block mb-3">Art der Teilnahme</label>
                <div className="flex flex-wrap gap-2.5">
                  {PARTICIPATION.map((p) => {
                    const active = form.participation === p;
                    return (
                      <button
                        key={p} type="button" onClick={() => update("participation", p)}
                        className={`rounded-full border px-4 py-2 text-sm transition-all duration-300 ${
                          active
                            ? "border-magenta bg-magenta text-white"
                            : "border-ink/20 text-ink/70 hover:border-magenta/60 hover:text-ink"
                        }`}
                        data-testid={`participation-${p.split(" ")[0].toLowerCase()}`}
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.25em] text-ink/40 block mb-3">Anzahl Gäste</label>
                <div className="flex items-center gap-5">
                  <button
                    type="button" onClick={() => update("guests", Math.max(1, form.guests - 1))}
                    className="rounded-full border border-ink/20 p-2.5 text-ink transition-colors hover:border-magenta hover:text-magenta"
                    data-testid="guests-decrement"
                  >
                    <Minus size={16} />
                  </button>
                  <span className="font-display text-3xl text-ink w-8 text-center" data-testid="guests-value">{form.guests}</span>
                  <button
                    type="button" onClick={() => update("guests", Math.min(20, form.guests + 1))}
                    className="rounded-full border border-ink/20 p-2.5 text-ink transition-colors hover:border-magenta hover:text-magenta"
                    data-testid="guests-increment"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.25em] text-ink/40 block mb-3">Extra</label>
                <button
                  type="button" onClick={() => update("teddy", !form.teddy)}
                  className={`flex w-full items-center justify-between rounded-2xl border p-4 text-left transition-all duration-300 ${
                    form.teddy ? "border-magenta bg-magenta/5" : "border-ink/15 hover:border-magenta/50"
                  }`}
                  data-testid="teddy-toggle"
                >
                  <span className="flex items-center gap-4">
                    <img src={IMAGES.teddy} alt="Teddybär" className="h-12 w-12 object-contain" />
                    <span>
                      <span className="block text-ink font-medium">Mit Teddybär</span>
                      <span className="block text-sm text-ink/50">CHF 100 statt CHF 80 pro Person</span>
                    </span>
                  </span>
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full border ${form.teddy ? "border-magenta bg-magenta text-white" : "border-ink/25 text-transparent"}`}>
                    <Check size={14} />
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {form.teddy && (
                    <motion.div
                      key="teddy-preview"
                      initial={{ opacity: 0, height: 0, scale: 0.6 }}
                      animate={{ opacity: 1, height: "auto", scale: 1 }}
                      exit={{ opacity: 0, height: 0, scale: 0.6 }}
                      transition={{ type: "spring", stiffness: 180, damping: 22 }}
                      className="mt-4 flex flex-col items-center overflow-hidden rounded-2xl bg-gradient-to-br from-violet/5 to-magenta/10 p-6"
                      data-testid="teddy-preview"
                    >
                      <motion.img
                        src={IMAGES.teddy}
                        alt="Ihr Benefiz-Teddybär"
                        className="h-48 w-48 object-contain drop-shadow-xl"
                        animate={{ y: [0, -10, 0], rotate: [-1.5, 1.5, -1.5] }}
                        transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
                      />
                      <p className="mt-3 text-center text-sm text-ink/60">Ihr Benefiz-Teddybär wartet schon auf Sie.</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.25em] text-ink/40">Nachricht (optional)</label>
                <textarea
                  rows={3} value={form.message} onChange={(e) => update("message", e.target.value)}
                  placeholder="Ihre Nachricht an uns" className={`${field} resize-none`} data-testid="input-message"
                />
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-ink/[0.04] px-5 py-4" data-testid="price-summary">
                <span className="text-sm text-ink/60">{form.guests} × CHF {perPerson}{form.teddy ? " (inkl. Teddybär)" : ""}</span>
                <span className="font-display text-2xl text-ink">Total CHF {total}</span>
              </div>
              <motion.button
                whileTap={{ scale: 0.98 }} type="submit" disabled={loading}
                className="group inline-flex items-center gap-3 rounded-full bg-ink px-9 py-4 text-sm font-medium text-ivory transition-colors duration-300 hover:bg-magenta disabled:opacity-60"
                data-testid="submit-registration"
              >
                {loading ? "Wird gesendet…" : "Anmeldung absenden"}
                <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </motion.button>
            </form>
          )}
        </div>
      </div>

      {/* Kontoangaben / Einzahlung */}
      <div className="mx-auto mt-16 max-w-6xl" data-testid="bank-details">
        <div className="grid grid-cols-1 gap-8 rounded-3xl border border-ink/10 bg-white p-6 sm:p-10 lg:grid-cols-[1fr_auto] lg:gap-14">
          <div>
            <p className="mb-6 text-xs uppercase tracking-[0.35em] text-violet">Kontoangaben für Einzahlung</p>
            <table className="w-full border-collapse">
              <tbody>
                {BANK.map(([label, value]) => (
                  <tr key={label} className="border-b border-ink/5 last:border-0" data-testid={`bank-row-${label.split(" ")[0].toLowerCase().replace(/[^a-z]/g, "")}`}>
                    <td className="py-3 pr-6 align-top text-sm text-ink/50 whitespace-nowrap">{label}</td>
                    <td className={`py-3 align-top font-medium text-ink ${label === "IBAN" ? "font-mono tracking-wide" : ""}`}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col items-center justify-center lg:border-l lg:border-ink/10 lg:pl-14">
            <p className="mb-4 text-xs uppercase tracking-[0.25em] text-ink/40">QR-Code</p>
            <img
              src={IMAGES.qrCode}
              alt="QR-Code für die Einzahlung"
              className="h-44 w-44 rounded-xl border border-ink/10 p-2"
              data-testid="bank-qr-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
